import { ApolloError } from 'apollo-client'
import { GetActivityFeedQuery, useGetActivityFeedQuery } from 'gqlTypes'
import excludeError from 'utils/excludeError'
import { isComment } from '../utils'
import { ShareWithoutVersion } from 'modules/versioning/ShareVersionContext/ShareVersionContext'

export interface UseGetActivityFeedProps {
  share: {
    userCanAccessVersions: boolean
    commentsEnabled: boolean
    identifier: string
    versionTypeAvailableToUser: ShareWithoutVersion['versionTypeAvailableToUser']
  }
  artboard: {
    identifier: string
    permanentArtboardShortId: string
  }
}

export interface UseGetActivityFeedResult {
  isLoading: boolean
  activities: GetActivityFeedQuery['activityFeed']['entries']
  error: ApolloError | undefined
  onLoadMore: () => Promise<void>
}

const filterEntriesWithoutPermission = (
  versionTypeAvailableToUser: ShareWithoutVersion['versionTypeAvailableToUser'],
  commentsEnabled: ShareWithoutVersion['commentsEnabled'],
  entries: GetActivityFeedQuery['activityFeed']['entries']
) =>
  entries.filter((entry, key) => {
    if (isComment(entry)) {
      return commentsEnabled ? entry : undefined
    }

    switch (versionTypeAvailableToUser) {
      case 'ANY':
        return entry
      case 'ANY_STARRED':
        if ('kind' in entry && entry.kind === 'PUBLISHED') {
          return entry
        }
        break
      case 'LATEST':
        // first one is the most recent
        if (key === 0) {
          return entry
        }
        break
    }
    return undefined
  })

export const useGetActivityFeed = ({
  artboard: { permanentArtboardShortId },
  share,
}: UseGetActivityFeedProps): UseGetActivityFeedResult => {
  const { networkStatus, data, fetchMore, error } = useGetActivityFeedQuery({
    /**
     * This is necessary to cleanup previous artboard data
     * when navigating between artboards
     */
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.permanentArtboardShortId !== curr?.permanentArtboardShortId,
    variables: {
      permanentArtboardShortId,
      shareIdentifier: share.identifier,
      includeArtboardDetails: share.userCanAccessVersions,
    },
    fetchPolicy: 'cache-and-network',
  })

  const after = data?.activityFeed?.meta.after || null
  const entries = data?.activityFeed?.entries || []
  const isLoading = networkStatus < 3

  const entriesWithPermissions = filterEntriesWithoutPermission(
    share.versionTypeAvailableToUser,
    share.commentsEnabled,
    entries
  )

  const onLoadMore = async ({ fetchLatest = false } = {}): Promise<void> => {
    if (!fetchLatest && !after) return Promise.resolve()

    try {
      await fetchMore({
        variables: {
          permanentArtboardShortId,
          after: fetchLatest ? null : after,
          includeArtboardDetails: share.userCanAccessVersions,
          ...(fetchLatest ? { limit: 10 } : {}),
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult?.activityFeed) {
            // TODO: report an error which should never happen
            // https://github.com/sketch-hq/Cloud/issues/1012
            return previousResult
          }
          const newActivities = fetchMoreResult?.activityFeed?.entries || []

          // If duplicated objects would be returned
          // Always prefer objects which were just recently received
          const newActivityIds = newActivities.map(x => x.identifier)
          const oldActivities = (
            previousResult.activityFeed?.entries || []
          ).filter(x => !newActivityIds.includes(x.identifier))

          const merged = [...oldActivities, ...newActivities]

          fetchMoreResult.activityFeed.entries = merged

          return fetchMoreResult
        },
      })
    } catch (err) {
      if (excludeError(err)) {
        return
      }
      throw err
    }
  }

  return {
    isLoading,
    activities: [...entriesWithPermissions].reverse(),
    error,
    onLoadMore,
  }
}
